import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/6e307a1a33ff6be0de40aa3d8bed16db.png" alt="TourDine" className="h-12 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#home" className={`text-sm font-light tracking-wide transition-colors ${scrolled ? 'text-gray-800 hover:text-amber-700' : 'text-white hover:text-amber-300'}`}>Home</a>
            <a href="#experiences" className={`text-sm font-light tracking-wide transition-colors ${scrolled ? 'text-gray-800 hover:text-amber-700' : 'text-white hover:text-amber-300'}`}>Experiences</a>
            <a href="#partners" className={`text-sm font-light tracking-wide transition-colors ${scrolled ? 'text-gray-800 hover:text-amber-700' : 'text-white hover:text-amber-300'}`}>Partners</a>
            <a href="#booking" className={`text-sm font-light tracking-wide transition-colors ${scrolled ? 'text-gray-800 hover:text-amber-700' : 'text-white hover:text-amber-300'}`}>Booking</a>
            <a href="#about" className={`text-sm font-light tracking-wide transition-colors ${scrolled ? 'text-gray-800 hover:text-amber-700' : 'text-white hover:text-amber-300'}`}>About</a>
            <a href="#contact" className={`text-sm font-light tracking-wide transition-colors ${scrolled ? 'text-gray-800 hover:text-amber-700' : 'text-white hover:text-amber-300'}`}>Contact</a>
          </div>
          <button className={`px-6 py-2.5 rounded-full text-sm font-light tracking-wide transition-all whitespace-nowrap cursor-pointer ${scrolled ? 'border border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white' : 'border border-white/40 text-white hover:bg-white/10'}`}>
            Reserve Experience
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/fdc1d64bb6cdf1b1ff94f8cdf7fab70b.jpeg" alt="TourDine Experience" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="text-7xl md:text-8xl lg:text-9xl font-serif text-white mb-6 tracking-wider leading-tight">
            Where Dining Becomes<br />
            <span className="italic font-light">A Cinematic Journey</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-12 max-w-3xl mx-auto font-light leading-relaxed tracking-wide">
            Immersive environments. Architectural excellence. Michelin-caliber cuisine. Experience the future of luxury dining.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href="#experiences" className="px-10 py-4 bg-amber-600 text-black text-sm font-medium tracking-wide rounded-full hover:bg-amber-500 transition-all cursor-pointer whitespace-nowrap">
              Explore Experiences
            </a>
            <a href="#partners" className="px-10 py-4 border border-white/40 text-white text-sm font-light tracking-wide rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap">
              Partner With Us
            </a>
          </div>
        </div>
      </section>

      {/* Experience Formats Section */}
      <section id="experiences" className="py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-20 gap-12">
            <div>
              <h2 className="text-6xl lg:text-7xl font-serif text-gray-900 leading-tight">
                Three Formats.<br />One Vision.
              </h2>
            </div>
            <div className="lg:w-[450px]">
              <p className="text-base text-gray-600 leading-relaxed">
                TourDine adapts to any venue and vision. From mobile luxury buses to permanent installations, we bring immersive dining experiences to hotels, events, and cultural destinations worldwide.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Luxury Dining Bus */}
            <div className="lg:col-span-2">
              <div className="w-full h-[500px] mb-6 rounded-3xl overflow-hidden">
                <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/cf6b6449d23dd94000912f2632e7e53a.jpeg" alt="Luxury Dining Bus" className="w-full h-full object-cover" />
              </div>
              <span className="inline-block px-4 py-1.5 bg-amber-100 text-gray-900 text-xs font-medium rounded-full mb-4">Mobile Experience</span>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">The Luxury Dining Bus</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                A fully drivable, double-decker dining experience featuring immersive projection technology, premium interiors, and world-class cuisine. Perfect for city tours, private events, and destination activations.
              </p>
            </div>

            {/* Hotel & Event Integration */}
            <div className="lg:col-span-1.5">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Hotel &amp; Event Integration</h3>
              <div className="w-full aspect-square rounded-3xl overflow-hidden mb-4">
                <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/13cbff00c5e008587294937a2d996f57.png" alt="Wooden Experience Bus" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Permanent Venues */}
            <div className="lg:col-span-1.5">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Permanent Venues</h3>
              <div className="w-full aspect-square rounded-3xl overflow-hidden mb-4">
                <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/e6657e6937d65c37bf23502326b2fc73.jpeg" alt="Permanent Installation" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative">
            <svg className="absolute -top-20 -left-10 w-32 h-32 text-gray-300 opacity-40" viewBox="0 0 100 100" fill="none">
              <path d="M20 80 Q 30 20, 80 30" stroke="currentColor" strokeWidth="2" />
            </svg>
            
            <div className="relative bg-[#1A1A1A] rounded-[48px] overflow-visible">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-[35%] relative -ml-0 lg:-ml-20 mt-8 lg:mt-0">
                  <div className="w-full h-[500px] lg:h-full rounded-2xl overflow-hidden">
                    <img src="https://public.readdy.ai/ai/img_res/b9af8aec141360ab952e60cf0b1b586b.jpg" alt="Guest" className="w-full h-full object-cover" />
                  </div>
                </div>
                
                <div className="lg:w-[65%] py-16 lg:py-20 px-8 lg:px-16 flex flex-col justify-center">
                  <div className="text-[#D4AF7A] text-8xl font-serif mb-6">&ldquo;</div>
                  <p className="text-2xl lg:text-3xl font-serif text-white leading-relaxed mb-10">
                    TourDine transcended every expectation. The seamless fusion of culinary artistry and cinematic storytelling created an evening we&apos;ll never forget. This is the future of luxury dining.
                  </p>
                  <div className="mb-12">
                    <p className="text-lg text-white font-medium mb-1">Alexandra Chen</p>
                    <p className="text-sm text-white/70">Private Celebration, Singapore</p>
                  </div>
                  <button className="self-start px-8 py-3.5 bg-[#E8C4B8] text-gray-900 text-sm font-medium rounded-full hover:bg-[#D4AF7A] transition-all cursor-pointer whitespace-nowrap">
                    Read More Stories
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="relative">
              <div className="w-full h-[600px] rounded-[32px] overflow-hidden">
                <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/49d38d3a55df6a047781f7665393fbb8.png" alt="TourDine Vision" className="w-full h-full object-cover" />
              </div>
              <div className="absolute top-6 left-6 flex gap-3">
                <span className="px-4 py-2 bg-white/20 backdrop-blur-md text-white text-xs font-medium rounded-2xl">Immersive</span>
                <span className="px-4 py-2 bg-white/20 backdrop-blur-md text-white text-xs font-medium rounded-2xl">Architectural</span>
                <span className="px-4 py-2 bg-white/20 backdrop-blur-md text-white text-xs font-medium rounded-2xl">Culinary</span>
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <h2 className="text-5xl lg:text-6xl font-serif text-gray-900 mb-6 leading-tight">
                Redefining Luxury Through Experience
              </h2>
              <div className="flex flex-wrap gap-3 mb-12">
                <span className="px-4 py-2 border border-gray-900 text-gray-900 text-sm rounded-full">Experiential Dining</span>
                <span className="text-sm text-gray-600 py-2">Cultural Storytelling</span>
                <span className="text-sm text-gray-600 py-2">Brand Partnerships</span>
              </div>

              <div className="bg-gray-900 rounded-[32px] p-8 lg:p-10">
                <span className="inline-block px-3 py-1 bg-[#D4AF7A] text-gray-900 text-xs font-medium rounded-full mb-6">Our Approach</span>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  <div className="lg:col-span-3">
                    <h3 className="text-3xl font-bold text-white mb-4">Built for Partnership &amp; Scale</h3>
                    <p className="text-base text-white/90 leading-relaxed">
                      TourDine operates through strategic collaborations with hotels, tourism boards, luxury brands, and cultural institutions. Our platform offers multiple revenue streams including ticketed experiences, private bookings, sponsorships, licensing, and venue integrations—creating a globally expandable, media-worthy dining attraction.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="w-full h-48 rounded-2xl overflow-hidden">
                      <img src="https://readdy.ai/api/search-image?query=business%20partnership%20handshake%20in%20luxury%20hospitality%20setting%20professional%20collaboration%20between%20executives%20modern%20corporate%20environment%20with%20elegant%20design%20representing%20strategic%20alliances%20and%20premium%20brand%20partnerships&width=500&height=400&seq=tourdine-partnership&orientation=landscape" alt="Partnership" className="w-full h-full object-cover" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Streams Section */}
      <section id="partners" className="py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-20 gap-12">
            <div>
              <h2 className="text-5xl lg:text-6xl font-serif text-gray-900 leading-tight">
                <span className="font-light text-gray-500">Multiple Streams.</span><br />
                <span className="italic">One Platform.</span>
              </h2>
            </div>
            <div className="lg:w-[420px]">
              <p className="text-base text-gray-700 leading-relaxed">
                TourDine&apos;s diverse revenue model creates value for partners, investors, and guests through ticketed experiences, private events, sponsorships, and venue licensing opportunities.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Private Events */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-full h-64 overflow-hidden">
                <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/fce6cf1defba6c7cece3507702cc0203.png" alt="Private Events" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <span className="inline-block px-4 py-1.5 bg-[#E8F5C8] text-gray-900 text-xs font-medium rounded-full mb-4">Private Events</span>
                <h3 className="text-2xl font-bold text-gray-900 leading-snug mb-3">Exclusive Celebrations &amp; Corporate Experiences</h3>
              </div>
            </div>

            {/* Sponsorships */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-full h-64 bg-gray-900 flex items-center justify-center overflow-hidden">
                <div className="w-24 h-24 flex items-center justify-center">
                  <i className="ri-hand-coin-line text-7xl text-[#D4AF7A]"></i>
                </div>
              </div>
              <div className="p-8">
                <span className="inline-block px-4 py-1.5 bg-[#E8F5C8] text-gray-900 text-xs font-medium rounded-full mb-4">Sponsorships</span>
                <h3 className="text-2xl font-bold text-gray-900 leading-snug mb-3">Brand Integration &amp; Cultural Showcases</h3>
              </div>
            </div>

            {/* Venue Licensing */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-full h-64 overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=luxury%20hotel%20destination%20with%20immersive%20dining%20venue%20integration%20premium%20resort%20restaurant%20partnership%20tourism%20hospitality%20collaboration%20elegant%20architectural%20space%20for%20experiential%20dining%20with%20sophisticated%20design%20and%20upscale%20ambiance&width=800&height=600&seq=tourdine-venue&orientation=landscape" alt="Venue Licensing" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <span className="inline-block px-4 py-1.5 bg-[#E8F5C8] text-gray-900 text-xs font-medium rounded-full mb-4">Venue Licensing</span>
                <h3 className="text-2xl font-bold text-gray-900 leading-snug mb-3">Hotel &amp; Destination Partnerships</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section id="booking" className="py-40 bg-[#F8F6F3]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="mb-16">
            <div className="w-full h-[400px] rounded-[28px] overflow-hidden mx-auto mb-12">
              <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/6540e8389ee32a5d5ce2c5e3735ad094.png" alt="Join TourDine" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl lg:text-5xl leading-relaxed">
              <span className="text-gray-500 font-light">Ready to</span><br />
              <span className="text-black font-bold"><span className="italic">transform</span> dining</span><br />
              <span className="text-gray-500 font-light">into an attraction?</span>
            </h2>
          </div>

          <a href="#contact" className="inline-block px-12 py-4 bg-black text-white text-base font-medium rounded-full hover:shadow-xl hover:shadow-amber-600/20 transition-all cursor-pointer whitespace-nowrap">
            Become a Partner
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-[#F8F6F3]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-serif text-gray-900 mb-6">Get in Touch</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Interested in partnering with TourDine or experiencing our immersive dining journey? We&apos;d love to hear from you.
            </p>
          </div>

          <form className="bg-white rounded-3xl p-10 lg:p-12 shadow-sm" data-readdy-form id="contact-form" onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const formData = new FormData(form);
            const data = new URLSearchParams();
            
            formData.forEach((value, key) => {
              data.append(key, value.toString());
            });

            try {
              const response = await fetch('https://readdy.ai/api/form/d5be3fhdn6dhfpiabk00', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: data.toString()
              });

              if (response.ok) {
                alert('Thank you for contacting TourDine! We will get back to you soon.');
                form.reset();
              } else {
                alert('Something went wrong. Please try again.');
              }
            } catch (error) {
              alert('Failed to send message. Please try again later.');
            }
          }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">First Name</label>
                <input type="text" name="first-name" required className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Last Name</label>
                <input type="text" name="last-name" required className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent" />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">Email</label>
              <input type="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent" />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">Interest</label>
              <select name="interest" required className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent cursor-pointer">
                <option value="">Select your interest</option>
                <option value="booking">Book an Experience</option>
                <option value="partnership">Partnership Opportunity</option>
                <option value="investment">Investment Inquiry</option>
                <option value="sponsorship">Sponsorship</option>
                <option value="media">Media &amp; Press</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-900 mb-2">Message</label>
              <textarea name="message" rows={5} maxLength={500} required className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent resize-none"></textarea>
              <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
            </div>

            <button type="submit" className="w-full py-4 bg-amber-600 text-black text-base font-medium rounded-full hover:bg-amber-500 transition-all cursor-pointer whitespace-nowrap">
              Send Message
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-[#0D2B2E] to-[#0A1F21] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" viewBox="0 0 1000 400" preserveAspectRatio="xMidYMid slice">
            <path d="M100,200 Q250,100 400,200 T700,200 Q850,150 950,200" stroke="currentColor" strokeWidth="1" fill="none" />
            <path d="M50,250 Q200,180 350,250 T650,250 Q800,200 900,250" stroke="currentColor" strokeWidth="1" fill="none" />
            <circle cx="200" cy="150" r="2" fill="currentColor" />
            <circle cx="500" cy="180" r="2" fill="currentColor" />
            <circle cx="800" cy="160" r="2" fill="currentColor" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-2 mb-8">
                <img src="https://static.readdy.ai/image/50d8c384cce10de0bf6396b740916ff6/6e307a1a33ff6be0de40aa3d8bed16db.png" alt="TourDine" className="h-10 w-auto brightness-0 invert" />
              </div>
              <div className="flex gap-3 mb-8">
                <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-3 bg-transparent border border-white/40 rounded-full text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/60" />
                <button className="w-12 h-12 flex items-center justify-center bg-amber-600 rounded-full hover:bg-amber-500 transition-colors cursor-pointer">
                  <i className="ri-notification-line text-xl text-black"></i>
                </button>
              </div>
              <p className="text-3xl lg:text-4xl font-serif leading-snug">
                Experience the future<br />of luxury dining
              </p>
            </div>

            <div className="lg:col-span-8 lg:pl-20">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div>
                  <h4 className="text-base font-medium mb-5">Experiences</h4>
                  <ul className="space-y-4">
                    <li><a href="#experiences" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Dining Formats</a></li>
                    <li><a href="#booking" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Private Events</a></li>
                    <li><a href="#experiences" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Cultural Journeys</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-base font-medium mb-5">Partnership</h4>
                  <ul className="space-y-4">
                    <li><a href="#partners" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Become a Partner</a></li>
                    <li><a href="#partners" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Sponsorship</a></li>
                    <li><a href="#partners" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Venue Integration</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-base font-medium mb-5">Company</h4>
                  <ul className="space-y-4">
                    <li><a href="#about" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">About TourDine</a></li>
                    <li><a href="#contact" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Press &amp; Media</a></li>
                    <li><a href="#contact" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Careers</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-base font-medium mb-5">Connect</h4>
                  <ul className="space-y-4">
                    <li><a href="#contact" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                    <li><a href="#booking" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Locations</a></li>
                    <li><a href="#contact" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">Newsletter</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-xs text-white/50">© 2025 TourDine. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg hover:border-white/60 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg hover:border-white/60 transition-colors cursor-pointer">
                <i className="ri-linkedin-line text-lg"></i>
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg hover:border-white/60 transition-colors cursor-pointer">
                <i className="ri-youtube-line text-lg"></i>
              </a>
              <a href="https://vimeo.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg hover:border-white/60 transition-colors cursor-pointer">
                <i className="ri-vimeo-line text-lg"></i>
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-white/50 hover:text-white/80 transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-white/30">|</span>
              <a href="#" className="text-xs text-white/50 hover:text-white/80 transition-colors cursor-pointer">Terms</a>
              <span className="text-white/30">|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 hover:text-white/80 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
